import { API_URL } from '../api';

// Compress video to max 1080p
export const compressVideo = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        // If metadata never loads (mobile background tab, unsupported codec), fall back after 5s
        const timeoutId = setTimeout(() => {
            console.warn('Video metadata load timed out — using original file.');
            resolve(file);
        }, 5000);

        video.onloadedmetadata = () => {
            clearTimeout(timeoutId);
            const canvas = document.createElement('canvas');
            let width = video.videoWidth;
            let height = video.videoHeight;

            // Scale down to 1080p max on longest side
            const maxDimension = 1080;
            if (width > height && width > maxDimension) {
                height = Math.round((height * maxDimension) / width);
                width = maxDimension;
            } else if (height > maxDimension) {
                width = Math.round((width * maxDimension) / height);
                height = maxDimension;
            }

            canvas.width = width;
            canvas.height = height;

            // Note: Client-side video re-encoding is complex.
            // For now, return original file and let server handle compression
            // This preserves video quality and only constrains dimensions metadata
            console.log(`Video will be resized to ${width}x${height} on server`);
            resolve(file);
        };
        video.onerror = () => {
            clearTimeout(timeoutId);
            reject(new Error('Failed to load video'));
        };
        video.src = URL.createObjectURL(file);
        video.load();
    });
};

// Compress image to max 2048px and 0.85 quality
export const compressImage = (file: File): Promise<Blob> => {
    if (file.type === 'image/gif') {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const buffer = e.target?.result as ArrayBuffer;
                if (buffer) resolve(new Blob([buffer], { type: 'image/gif' }));
                else reject(new Error('Failed to read GIF buffer'));
            };
            reader.onerror = () => reject(new Error('Failed to read GIF'));
            reader.readAsArrayBuffer(file);
        });
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Resize if larger than 2048px on longest side (maintain quality)
                const maxDimension = 2048;
                if (width > height && width > maxDimension) {
                    height = Math.round((height * maxDimension) / width);
                    width = maxDimension;
                } else if (height > maxDimension) {
                    width = Math.round((width * maxDimension) / height);
                    height = maxDimension;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);
                // Use 0.85 quality for high quality but compressed JPEGs
                canvas.toBlob((blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error('Canvas compression failed'));
                }, 'image/jpeg', 0.85);
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
};

// Compress audio losslessly (convert to efficient codec if needed)
export const compressAudio = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        // For audio, we return the file as-is for lossless compression on server
        console.log('Audio will be losslessly compressed on server');
        resolve(file);
    });
};

// Upload file to S3 or fallback to local server
export const uploadMediaFile = (
    file: File,
    currentUserId: string,
    onProgress?: (progress: number) => void
): Promise<string> & { xhr?: XMLHttpRequest } => {
    let activeXhr: XMLHttpRequest | undefined;

    const promise = new Promise<string>(async (resolve, reject) => {
        // Primary path: Upload to local or production server using XMLHttpRequest.
        // This streams the file (negligible client-side memory footprint, preventing iOS Safari crashes),
        // provides accurate, granular 1-100% progress updates, and enables background video compression.
        const attemptServerUpload = () => {
            return new Promise<string>((serverResolve, serverReject) => {
                let serverUrl = API_URL.replace(/\/api\/?$/, '');

                // Force HTTPS for non-local hosts
                const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('10.');
                if (!isLocal && serverUrl.startsWith('http:')) {
                    serverUrl = serverUrl.replace('http:', 'https:');
                }

                const formData = new FormData();
                formData.append('file', file);

                const xhr = new XMLHttpRequest();
                activeXhr = xhr;
                xhr.open('POST', `${serverUrl}/api/uploads`, true);

                // Allow 10 minutes for large file uploads before giving up
                xhr.timeout = 10 * 60 * 1000;

                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        const percentComplete = Math.round((event.loaded / event.total) * 100);
                        onProgress(percentComplete);
                    }
                };

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const data = JSON.parse(xhr.responseText);
                            if (data.url) {
                                console.log('✅ File uploaded to server successfully:', data.url);
                                if (onProgress) onProgress(100);
                                serverResolve(data.url);
                            } else {
                                serverReject(new Error('No URL received from server'));
                            }
                        } catch (e) {
                            serverReject(new Error('Invalid response from server'));
                        }
                    } else {
                        console.error('⚠️ Server responded with error:', xhr.status);
                        serverReject(new Error(`Server error: ${xhr.status}`));
                    }
                };

                xhr.onerror = () => {
                    console.error('⚠️ Upload failed: Network Error');
                    serverReject(new Error('Network error occurred during upload'));
                };

                xhr.ontimeout = () => {
                    console.error('⚠️ Upload timed out after 10 minutes');
                    serverReject(new Error('Upload timed out — please check your connection and try again'));
                };

                xhr.onabort = () => {
                    console.log('Upload cancelled by user');
                    serverReject(new Error('Upload cancelled'));
                };

                xhr.send(formData);
            });
        };

        try {
            const url = await attemptServerUpload();
            resolve(url);
        } catch (serverError) {
            console.warn('⚠️ Server upload failed/unreachable. Attempting fallback:', serverError);

            // Fallback: If server fails and S3 credentials are set, attempt direct S3 upload
            if (process.env.REACT_APP_AWS_ACCESS_KEY_ID && process.env.REACT_APP_AWS_S3_BUCKET) {
                console.log('🚀 Triggering fallback S3 client-side upload...');
                try {
                    // @ts-ignore: optional aws sdk
                    const { S3Client, PutObjectCommand } = await import('@aws-sdk/client-s3');

                    const s3Client = new S3Client({
                        region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
                        credentials: {
                            accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID!,
                            secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY!,
                        }
                    });

                    // Convert File to ArrayBuffer for S3 SDK compatibility
                    const arrayBuffer = await file.arrayBuffer();
                    const buffer = new Uint8Array(arrayBuffer);

                    const key = `uploads/${currentUserId}/${Date.now()}-${file.name}`;

                    const command = new PutObjectCommand({
                        Bucket: process.env.REACT_APP_AWS_S3_BUCKET,
                        Key: key,
                        Body: buffer,
                        ContentType: file.type,
                    });

                    await s3Client.send(command);
                    const url = `https://${process.env.REACT_APP_AWS_S3_BUCKET}.s3.${process.env.REACT_APP_AWS_REGION || 'us-east-1'}.amazonaws.com/${key}`;
                    console.log('✅ File uploaded fallback S3 successfully:', url);
                    if (onProgress) onProgress(100);
                    resolve(url);
                } catch (s3Error) {
                    console.error('❌ Both server upload and fallback S3 upload failed:', s3Error);
                    reject(s3Error);
                }
            } else {
                reject(serverError);
            }
        }
    });

    const extendedPromise = promise as Promise<string> & { xhr?: XMLHttpRequest };
    Object.defineProperty(extendedPromise, 'xhr', {
        get: () => activeXhr
    });

    return extendedPromise;
};

export const deleteMediaFile = async (url: string) => {
    if (!url) return;

    // Check for S3 URL
    if (process.env.REACT_APP_AWS_S3_BUCKET && url.includes(`${process.env.REACT_APP_AWS_S3_BUCKET}.s3.amazonaws.com`)) {
        try {
            // @ts-ignore
            const { S3Client, DeleteObjectCommand } = await import('@aws-sdk/client-s3');
            const s3Client = new S3Client({
                region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
                credentials: {
                    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID!,
                    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY!,
                }
            });

            // Extract Key from URL
            const bucketUrl = `https://${process.env.REACT_APP_AWS_S3_BUCKET}.s3.amazonaws.com/`;
            if (url.startsWith(bucketUrl)) {
                const key = url.replace(bucketUrl, '');
                const command = new DeleteObjectCommand({
                    Bucket: process.env.REACT_APP_AWS_S3_BUCKET,
                    Key: key,
                });

                await s3Client.send(command);
                console.log('✅ File deleted from S3:', key);
            }
        } catch (error) {
            console.error('⚠️ Failed to delete file from S3:', error);
        }
    } else {
        // Local server fallback
        try {
            let serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

            // If accessing via IP address (mobile), try to use the same IP
            const currentUrl = window.location.href;
            if (currentUrl.includes('192.168') || currentUrl.includes('10.0') || /\d+\.\d+\.\d+\.\d+/.test(window.location.hostname)) {
                const ipAddress = window.location.hostname;
                serverUrl = `${window.location.protocol}//${ipAddress}:5000`;
            }

            const deleteUrl = `${serverUrl}/api/uploads?url=${encodeURIComponent(url)}`;
            const response = await fetch(deleteUrl, { method: 'DELETE' });

            if (response.ok) {
                console.log('✅ File deleted from local server:', url);
            } else {
                console.warn('⚠️ Failed to delete file from local server:', response.statusText);
            }
        } catch (e) {
            console.error('⚠️ Error deleting from local server:', e);
        }
    }
};

export const resolveLocalUrl = (url: string): string => {
    if (!url) return url;
    
    // Regex matching http://localhost:5000, http://127.0.0.1:5000, http://192.168.x.x:5000, http://10.x.x.x:5000, etc.
    const localHostRegex = /^(https?:\/\/)(localhost|127\.0\.0\.1|192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(?:1[6-9]|2\d|3[0-1])\.\d+\.\d+)(:5000)?(\/.*)$/i;
    
    if (typeof window !== 'undefined' && localHostRegex.test(url)) {
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        
        // If current hostname is a local domain or IP, rewrite the host to match the current access point
        if (
            hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname.startsWith('192.168.') ||
            hostname.startsWith('10.') ||
            hostname.startsWith('172.')
        ) {
            return url.replace(localHostRegex, (match, proto, host, port, path) => {
                return `${protocol}//${hostname}:5000${path}`;
            });
        }
    }
    
    return url;
};

